import { motion } from 'framer-motion'
import React from 'react'
import { Link } from 'react-scroll'
import { Column, Row, Text } from '@/components/core'
import { classNameBuilder } from '@/helpers'
import { useScrollPosition } from '@/hooks/useScrollPosition'

const NAVBAR_ITEMS: {
  id: ItemsType
  label: string
  markerColor: MarkerColorType
}[] = [
  { id: 'result', label: 'result_nav_bar_result', markerColor: '#09D4E1' },
  {
    id: 'fluency-analysis',
    label: 'result_nav_bar_fluency_analysis',
    markerColor: '#F99603'
  },
  {
    id: 'grammar-analysis',
    label: 'result_nav_bar_grammar_analysis',
    markerColor: '#03A0F9'
  },
  {
    id: 'conversation-recording',
    label: 'result_nav_bar_conversation_recording',
    markerColor: '#08AD85'
  }
]

type MarkerColorType = '#09D4E1' | '#F99603' | '#03A0F9' | '#08AD85'
type ItemsType =
  | 'result'
  | 'fluency-analysis'
  | 'grammar-analysis'
  | 'conversation-recording'

export const ResultNavbar: React.FC = () => {
  const scrollPosition = useScrollPosition()
  const [selectedItem, setSelectedItem] = React.useState<string>('result')
  React.useEffect(() => {
    NAVBAR_ITEMS.some((item) => {
      const position = document.getElementById(item.id)?.offsetTop
      if (position && scrollPosition > position - 65) setSelectedItem(item.id)
    })
  }, [scrollPosition])

  React.useEffect(() => {
    const navbar = document.getElementById(`navbar`)
    const navbarItem = document.getElementById(`navbar-item-${selectedItem}`)
    navbar?.scroll({ left: navbarItem?.offsetLeft, behavior: 'smooth' })
  }, [selectedItem])

  const variants = {
    compact: {
      width: 790,
      borderBottomLeftRadius: 16,
      borderBottomRightRadius: 16
    },
    full: { width: '100%' }
  }
  return (
    <motion.div
      id="navbar"
      className={classNameBuilder(
        'scrollbar-none fixed top-0 z-[100] h-14 self-center bg-[#28016699] backdrop-blur-xl max-lg:!w-full max-lg:overflow-x-auto max-lg:!rounded-none'
      )}
      animate={scrollPosition > 50 ? 'full' : 'compact'}
      transition={{
        duration: 0.5,
        ease: 'easeOut'
      }}
      variants={variants}
    >
      <Row className="mx-auto h-full items-center justify-between px-4 lg:w-full lg:max-w-[790px] lg:px-20">
        {NAVBAR_ITEMS.map((props) => (
          <NavbarItem
            key={props.id}
            scrollTo={props.id}
            active={selectedItem === props.id}
            {...props}
          />
        ))}
      </Row>
    </motion.div>
  )
}

export default ResultNavbar

interface NavbarItemProps {
  id: string
  label: string
  active?: boolean
  markerColor?: MarkerColorType
  onClick?: () => void
  scrollTo?: string
}
const NavbarItem: React.FC<NavbarItemProps> = ({
  id,
  label,
  active,
  markerColor = '#09D4E1',
  onClick,
  scrollTo
}) => {
  return (
    <Link
      to={scrollTo || ''}
      spy={true}
      smooth={true}
      offset={-60}
      duration={500}
      onClick={onClick}
      className="h-full"
    >
      <Column
        id={`navbar-item-${id}`}
        className="group relative h-full cursor-pointer select-none items-center justify-center max-lg:px-4"
      >
        <Text.Translated
          translationKey={label}
          size="xs"
          fontWeight="bold"
          transform="uppercase"
          className={classNameBuilder(
            'whitespace-nowrap text-white',
            'transition-colors duration-300',
            active
              ? ''
              : 'text-opacity-40 group-hover:text-opacity-90 group-active:text-opacity-100'
          )}
        />
        <div
          className={classNameBuilder(
            'absolute bottom-2 h-2 w-2 rounded-full',
            'transition-colors duration-300',
            active ? markerColorMap[markerColor] : ''
          )}
        />
      </Column>
    </Link>
  )
}

const markerColorMap: Record<MarkerColorType, string> = {
  '#03A0F9': 'bg-[#03A0F9]',
  '#08AD85': 'bg-[#08AD85]',
  '#09D4E1': 'bg-[#09D4E1]',
  '#F99603': 'bg-[#F99603]'
}
